"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

import {
  useCreateOrderMutation,
} from "@/features/api/orderApi";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth || {});
const [isRedirecting, setIsRedirecting] = useState(false);
  /* --------------------------------------------------
     PRODUCT NORMALIZATION (Course + Mock Test)
  -------------------------------------------------- */
  const product = useMemo(() => {
    const params = new URLSearchParams(location.search);

    // Course via query params
    if (params.has("courseId")) {
      return {
        id: params.get("courseId"),
        name: params.get("courseName") || "Course",
        price: Number(params.get("price")) || 0,
        image: params.get("courseImage") || "",
        type: "COURSE",
      };
    }

    // Mock test via navigate state
    if (location.state?.product) {
      const p = location.state.product;
      return {
        id: p.id,
        name: p.name,
        price: Number(p.price) || 0,
        image: p.image || "",
        type: p.productType || "ONLINE_order",
      };
    }

    return null;
  }, [location]);

  /* --------------------------------------------------
     REDIRECT IF INVALID
  -------------------------------------------------- */
  useEffect(() => {
    if (!product) {
      toast.error("No product selected.");
      navigate("/", { replace: true });
    }
  }, [product, navigate]);

  /* --------------------------------------------------
     FORM STATE
  -------------------------------------------------- */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);

  /* --------------------------------------------------
     AUTO FILL LOGGED IN USER
  -------------------------------------------------- */
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData({
        name: user?.name || "",
        email: user?.email || "",
        phone: user?.phone || user?.mobile || "",
      });
    }
  }, [isAuthenticated, user]);

  /* --------------------------------------------------
     TAX CALCULATION (18% GST)
  -------------------------------------------------- */
  const price = Number(product?.price || 0);
  const cgst = price > 0 ? Number((price * 0.09).toFixed(2)) : 0;
  const sgst = price > 0 ? Number((price * 0.09).toFixed(2)) : 0;
  const totalAmount =
    price > 0 ? Number((price + cgst + sgst).toFixed(2)) : 0;

  /* --------------------------------------------------
     API
  -------------------------------------------------- */
  const [createOrder, { isLoading }] = useCreateOrderMutation();

  /* --------------------------------------------------
     HANDLERS
  -------------------------------------------------- */
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const validateForm = () => {
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.replace(/\D/g, "");

    if (!name || !email || !phone) {
      toast.error("All fields are required.");
      return false;
    }

    if (!/^\d{10}$/.test(phone)) {
      toast.error("Enter valid 10 digit phone number.");
      return false;
    }

    if (!termsAccepted) {
      toast.error("Please accept Terms & Conditions.");
      return false;
    }

    return true;
  };

  /* --------------------------------------------------
     MAIN order LOGIC
  -------------------------------------------------- */
const handleSubmit = async (e) => {
  e.preventDefault();

  if (isRedirecting) return;
  if (!validateForm()) return;

  const cleanPhone = formData.phone.replace(/\D/g, "");

  try {
    setIsRedirecting(true);

    const response = await createOrder({
      amount: totalAmount,
      phone: cleanPhone,
      courseId: product.id,
      courseName: product.name,
      name: formData.name.trim(),
      email: formData.email.trim(),
      productType: product.type,
    }).unwrap();

    // Paid flow
    if (response?.redirectUrl) {
      window.location.href = response.redirectUrl;
      return;
    }

    // Free flow
    if (response?.transactionId) {
      navigate(
        `/payment-status?orderId=${response.transactionId}`,
        { replace: true }
      );
      return;
    }

    setIsRedirecting(false);
    toast.error("Unable to initiate payment.");
  } catch (error) {
    setIsRedirecting(false);
    console.error("Payment Error:", error);
    toast.error(error?.data?.message || "Payment failed");
  }
};
  if (!product) return null;

  /* --------------------------------------------------
     UI
  -------------------------------------------------- */
  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-8">
        
        {/* ---------------- BILLING ---------------- */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Billing Details</CardTitle>
              <CardDescription>
                Enter your details to complete purchase
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-5 w-5 cursor-pointer accent-blue-600"
                />
                <Label className="text-sm">
                  I agree to{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline"
                  >
                    Terms & Conditions
                  </a>
                </Label>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ---------------- SUMMARY ---------------- */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              {product.image && (
                <img
                  src={product.image}
                  alt={product.name}
                  className="rounded-lg"
                  onError={(e) =>
                    (e.currentTarget.style.display = "none")
                  }
                />
              )}

              <h2 className="font-semibold text-lg">
                {product.name}
              </h2>

              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span>Price</span>
                  <span>₹{price.toFixed(2)}</span>
                </div>

                {price > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span>CGST (9%)</span>
                      <span>₹{cgst.toFixed(2)}</span>
                    </div>

                    <div className="flex justify-between">
                      <span>SGST (9%)</span>
                      <span>₹{sgst.toFixed(2)}</span>
                    </div>
                  </>
                )}

                <hr />

                <div className="flex justify-between font-bold">
                  <span>Total</span>
                  <span>₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isLoading || isRedirecting}
                className="w-full"
              >
                {(isLoading || isRedirecting) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}

                {isRedirecting
                  ? "Redirecting..."
                  : totalAmount === 0
                  ? "Enroll Now"
                  : "Proceed to Pay"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}